import axios from 'axios';
import { authService } from './authService';

// Webmail.API lives on :5002. Platform.Auth (the auth/* endpoints) lives on :5001
// and is consumed via authService.js — DO NOT route auth calls through this client.
// Controllers are versioned (api/v{version}/[controller]) so the base MUST include /v1;
// service paths stay version-less (e.g. '/mailboxes', '/agents/summary/...').
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 120000 // 2 minutes for AI operations
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('webmail_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Single-flight refresh: coalesce simultaneous 401s onto one refresh call.
let refreshPromise = null;

const skipRefreshFor = (url) =>
  !url ||
  url.includes('/auth/refresh') ||
  url.includes('/auth/login') ||
  url.includes('/auth/sso/exchange') ||
  url.includes('/auth/logout');

const clearAndBounce = () => {
  localStorage.removeItem('webmail_token');
  localStorage.removeItem('webmail_refresh_token');
  localStorage.removeItem('webmail_user');
  window.location.href = '/login';
};

api.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      skipRefreshFor(originalRequest.url)
    ) {
      if (status === 401 && skipRefreshFor(originalRequest?.url)) {
        clearAndBounce();
      }
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (!refreshPromise) {
      refreshPromise = authService.refreshToken()
        .then((newToken) => {
          refreshPromise = null;
          return newToken;
        })
        .catch((err) => {
          refreshPromise = null;
          throw err;
        });
    }

    try {
      const newToken = await refreshPromise;
      if (!newToken) {
        clearAndBounce();
        return Promise.reject(error);
      }
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshErr) {
      clearAndBounce();
      return Promise.reject(error);
    }
  }
);

export default api;
